import { useState, useCallback, useEffect } from 'react'
import { useMutation } from '@tanstack/react-query'
import { useMediaQuery } from 'react-responsive'
import { bid } from '../../../api-client/auction/bid'
import { AuctionStatus, PriceComponentType } from '../../../api-client/enums'
import { useAuth } from '../../../contexts/auth'
import { Button } from '../../../components/button'
import Modal from '../../../components/modal'
import { PriceFormField } from '../../../components/inputs'
import { Desktop, Mobile } from '../../../components/media'
import CannotBuyYourVehicle from '../modals/cannot-buy-your-vehicle'
import BuyFeeAgreement from '../modals/buy-fee-agreement'
import GetVerified from '../modals/get-verified'
import HighBid from '../modals/high-bid'
import useVehiclePageData from '../use-vehicle-page-data'
import { GA_TRACKING_ID2 } from '../../../utils/g-tag'

const errorMessages = {
  OUTBID: 'Bid should be more than current top bid',
}

const MAX_BID_WARNING_COEFFICIENT = 9

export default function BidInput({ shipmentRate, isDealerVehicle }) {
  const isMobile = useMediaQuery({ query: '(max-width: 1024px)' })
  const [isInputVisible, setIsInputVisible] = useState(false)
  const [bidAmount, setBidAmount] = useState('')
  const [warning, setWarning] = useState(null)
  const [isGetVerifiedModalOpen, setIsGetVerifiedModalOpen] = useState(false)
  const [isCannotBuyYourOwnVehicleModalOpen, setIsCannotBuyYourOwnVehicleModalOpen] = useState(
    false,
  )
  const [isBuyFeeAgreementVisible, setIsBuyFeeAgreementVisible] = useState(false)
  const [isHighBidModalOpen, setIsHighBidModalOpen] = useState(false)
  const [{ userDetails }] = useAuth()

  const { vehicleData, auctionInfo } = useVehiclePageData()
  const [acceptedPrices, setAcceptedPrices] = useState([])

  const buyFee = Number(process.env.NEXT_PUBLIC_BUY_FEE)

  useEffect(() => {
    const shippingFee = shipmentRate?.isSucceeded ? shipmentRate.rate : 0
    setAcceptedPrices([
      { amount: bidAmount, type: PriceComponentType.VehiclePrice },
      { amount: buyFee, type: PriceComponentType.ServiceFee },
      { amount: shippingFee, type: PriceComponentType.ShippingFee },
    ])
  }, [shipmentRate, bidAmount, buyFee])

  const { mutate: bidMutation, isLoading: isBidding } = useMutation(
    {
      mutationFn: () => bid({ auctionId: vehicleData.auctionId, bidAmount, acceptedPrices }),
      onSuccess: () => {
        setBidAmount('')
        if (isMobile) setIsInputVisible(false)
      },
      onError: ({ error }) => {
        setWarning(errorMessages[error.code] || error.message)
      },
    },
  )

  const handleBid = () => {
    if (bidAmount) {
      if (
        auctionInfo.currentUserBidAmount === null &&
        !localStorage.getItem('isInitialBidBuyAgreementAccepted')
      ) {
        setIsBuyFeeAgreementVisible(true)
      } else if (+bidAmount > auctionInfo.topBidAmount * MAX_BID_WARNING_COEFFICIENT) {
        setIsHighBidModalOpen(true)
      } else bidMutation()
    }
  }

  const handleBidChange = (event) => {
    if (warning) setWarning(null)
    setBidAmount(event.target.value.replace(/\./g, ''))
  }

  const handleBidClick = () => {
    try {
      //this can throw an exception when blocked by adblockers
      window.gtag('event', 'conversion', { send_to: GA_TRACKING_ID2 + '/eU34CL3CoooCELuM0rsB' })
    } catch {}

    if (userDetails.verified && !auctionInfo.currentUserIsSeller) setIsInputVisible((prev) => !prev)
    else if (auctionInfo.currentUserIsSeller) setIsCannotBuyYourOwnVehicleModalOpen(true)
    else setIsGetVerifiedModalOpen(true)
  }

  const handleGetVerifiedModalClose = useCallback(() => setIsGetVerifiedModalOpen(false), [])
  const handleCannotBuyYourVehicleModalClose = useCallback(
    () => setIsCannotBuyYourOwnVehicleModalOpen(false),
    [],
  )

  useEffect(
    () => () =>
      localStorage.getItem('isInitialBidBuyAgreementAccepted') &&
      localStorage.removeItem('isInitialBidBuyAgreementAccepted'),
    [],
  )

  if (!auctionInfo || auctionInfo.auctionStatus !== AuctionStatus.Live) return null

  return (
    <div className='bidInput'>
      <Button className='bidBtn' onClick={handleBidClick}>
        Bid
      </Button>

      <Desktop>
        {isInputVisible && (
          <div className='inputBox'>
            <label>
              <span>Enter your bid</span>
              <PriceFormField
                autoFocus
                disabled={isBidding}
                value={bidAmount}
                onChange={handleBidChange}
                adornment={
                  <Button
                    disabled={isBidding}
                    size='sm'
                    color='success'
                    className='confirmBtn'
                    onClick={handleBid}
                  >
                    Confirm
                  </Button>
                }
              />
            </label>
            {warning && <p className='warning'>{warning}</p>}
          </div>
        )}
      </Desktop>

      <Mobile>
        {isInputVisible && (
          <Modal onClose={() => setIsInputVisible(false)}>
            <div className='inputBox'>
              <label>
                <span className='title'>Top Bid</span>
                <p>
                  The current top bid is{' '}
                  <span className='topBid'>${auctionInfo.topBidAmount.toLocaleString()}</span>
                </p>
                <span className='title'>Enter your bid</span>
                <PriceFormField
                  autoFocus
                  disabled={isBidding}
                  value={bidAmount}
                  onChange={handleBidChange}
                />
              </label>
              {warning && <p className='warning'>{warning}</p>}
              <div className='buttonsContainer flex'>
                <Button color='secondary' onClick={() => setIsInputVisible(false)}>
                  Cancel
                </Button>
                <Button disabled={isBidding} onClick={handleBid}>
                  Confirm Bid
                </Button>
              </div>
            </div>
          </Modal>
        )}
      </Mobile>

      {isGetVerifiedModalOpen && <GetVerified onClose={handleGetVerifiedModalClose} />}
      {isCannotBuyYourOwnVehicleModalOpen && (
        <CannotBuyYourVehicle onClose={handleCannotBuyYourVehicleModalClose} />
      )}
      {isBuyFeeAgreementVisible && (
        <BuyFeeAgreement
          onClose={() => setIsBuyFeeAgreementVisible(false)}
          onAccept={() => {
            setIsBuyFeeAgreementVisible(false)
            localStorage.setItem('isInitialBidBuyAgreementAccepted', 'true')
            handleBid()
          }}
          buyFee={process.env.NEXT_PUBLIC_BUY_FEE}
          shipmentRate={shipmentRate}
          isDealerVehicle={isDealerVehicle}
        />
      )}
      {isHighBidModalOpen && (
        <HighBid
          onClose={() => setIsHighBidModalOpen(false)}
          onAccept={() => {
            setIsHighBidModalOpen(false)
            bidMutation()
          }}
        />
      )}

      <style jsx>{`
        .bidInput {
          margin-bottom: 1rem;

          :global(.bidBtn) {
            display: block;
            margin: 0 auto;
            min-width: 10.6rem;
            height: 3.5rem;
            font-size: 1.4rem;
          }

          :global(.bidBtn:active) {
            opacity: 1;
          }

          :global(.confirmBtn) {
            position: relative;
            right: -1rem;
          }
        }

        .inputBox {
          position: relative;
          margin: 1.4rem 0 0;
          background: var(--primary-color);
          border-radius: 1rem;
          padding: 0.6rem 2rem 2.2rem;

          &::before {
            content: '';
            position: absolute;
            top: -1.7rem;
            left: 50%;
            width: 1.5rem;
            height: 1.5rem;
            background: inherit;
            transform: rotate(45deg) translateX(-50%);
            pointer-events: none;
          }

          label {
            display: block;

            span {
              display: block;
              margin: 0 0 0.5rem;
              color: #fff;
              text-align: center;
              font-size: 1.4rem;
              font-weight: 700;
            }
          }

          .warning {
            position: absolute;
            left: 0;
            bottom: 0.3rem;
            width: 100%;
            text-align: center;
            color: #fff;
            font-weight: 700;
            font-size: 1.3rem;
          }
        }

        @media (max-width: 1024px) {
          .bidInput {
            margin: 0;
          }

          .inputBox {
            margin: 5rem 0 0;
            padding: 0 1.5rem;
            background: transparent;

            &::before {
              display: none;
            }

            label {
              .title {
                margin: 0 0 1.1rem;
                color: var(--primary-text-color);
                font-size: 1.8rem;
              }

              p {
                margin: 0 0 2.5rem;
                font-size: 1.4rem;
                text-align: center;
              }

              .topBid {
                display: inline-block;
                color: var(--primary-color);
                font-weight: 500;
              }
            }

            .warning {
              position: relative;
              left: unset;
              bottom: unset;
              margin: 0.5rem 0 0;
              color: var(--primary-text-color);
            }

            .buttonsContainer {
              position: fixed;
              bottom: 2.1rem;
              left: 1.5rem;
              width: calc(100vw - 3rem);

              :global(button) {
                width: 100%;
                margin: 0 0.35rem;
              }
            }
          }
        }
      `}</style>
    </div>
  )
}
